import React from "react"
import { Badge, Card, Thumbnail } from "@shopify/polaris"
import PropTypes from "prop-types"
import { CodeMajor } from "@shopify/polaris-icons"
import { useTranslation } from "react-i18next"
import "stylesheets/DeveloperPortal.scss"

/**
 * Compact reusable API connector card to support multiple integrations.
 * Contains a visual, service name, description, tag (top-right) and status (bottom).
 */
const ApiConnectorCard = ({
  serviceName,
  description,
  tag,
  status,
  statusLabel,
  logoSource,
  logoAlt,
  onStatusClick,
  fillIcon = false,
  documentationUrl,
}) => {
  const { t } = useTranslation()

  const handleStatusClick = (e) => {
    if (documentationUrl) {
      // Redirect to documentation
      window.open(documentationUrl, "_blank", "noopener,noreferrer")
      return
    }
    if (onStatusClick && status === "configure_now") {
      e.preventDefault()
      onStatusClick()
    }
  }

  const getStatusLabel = () => {
    // Allow custom label override
    if (statusLabel) {
      return statusLabel
    }
    if (documentationUrl) {
      return t("developer_portal:status_labels.view_documentation", {
        defaultValue: "View documentation",
      })
    }
    switch (status) {
      case "configure_now":
        return t("developer_portal:status_labels.configure_now", {
          defaultValue: "Configure now",
        })
      case "connected":
        return t("developer_portal:status_labels.connected", {
          defaultValue: "Connected",
        })
      case "synchronizing":
        return t("developer_portal:status_labels.synchronizing", {
          defaultValue: "Synchronizing",
        })
      case "error":
        return t("developer_portal:status_labels.error", {
          defaultValue: "Error",
        })
      default:
        return status
    }
  }

  const isStatusClickable =
    documentationUrl || (status === "configure_now" && onStatusClick)

  // Detect if it's an image (string) or a Polaris icon (object/component)
  // Imported images (PNG, SVG, etc.) are strings (URLs)
  // Polaris icons are components/objects
  const isImage =
    logoSource &&
    (typeof logoSource === "string" ||
      (typeof logoSource === "object" && logoSource.default))

  const logoUrl =
    typeof logoSource === "object" && logoSource.default
      ? logoSource.default
      : logoSource

  return (
    <div className="apiConnectorCard">
      <Card>
        <Card.Section>
          <div className="apiConnectorCard__top">
            <div className="apiConnectorCard__header">
              <div className="apiConnectorCard__logoContainer">
                {isImage ? (
                  <img
                    src={logoUrl}
                    alt={logoAlt || serviceName}
                    className={`apiConnectorCard__logo ${
                      fillIcon ? "apiConnectorCard__logo--fill" : ""
                    }`}
                  />
                ) : (
                  <Thumbnail
                    size="medium"
                    alt={logoAlt || serviceName}
                    source={logoSource || CodeMajor}
                  />
                )}
              </div>
              <div className="apiConnectorCard__titleWrapper">
                {tag && (
                  <div className="apiConnectorCard__tag">
                    <Badge>{tag}</Badge>
                  </div>
                )}
                <h2 className="apiConnectorCard__serviceName">{serviceName}</h2>
              </div>
            </div>
            <p className="apiConnectorCard__description">{description}</p>
          </div>
          {(status || documentationUrl) && (
            <div
              className={`apiConnectorCard__status ${
                documentationUrl
                  ? "apiConnectorCard__status--documentation"
                  : `apiConnectorCard__status--${status}`
              }`}
            >
              {isStatusClickable ? (
                <span
                  className="apiConnectorCard__statusText apiConnectorCard__statusText--clickable"
                  onClick={handleStatusClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      handleStatusClick(e)
                    }
                  }}
                >
                  {getStatusLabel()}
                </span>
              ) : (
                <span className="apiConnectorCard__statusText">
                  {getStatusLabel()}
                </span>
              )}
            </div>
          )}
        </Card.Section>
      </Card>
    </div>
  )
}

ApiConnectorCard.propTypes = {
  serviceName: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  tag: PropTypes.string,
  status: PropTypes.oneOf([
    "configure_now",
    "connected",
    "synchronizing",
    "error",
  ]),
  statusLabel: PropTypes.string,
  logoSource: PropTypes.any,
  logoAlt: PropTypes.string,
  onStatusClick: PropTypes.func,
  fillIcon: PropTypes.bool,
  documentationUrl: PropTypes.string,
}

export default ApiConnectorCard
